import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Tooltip } from "../Tooltip";
import type { UnderlineTabsProps } from "./types";
import styles from "./UnderlineTabs.module.css";

const formatCssSize = (value: number | string) =>
  typeof value === "number" ? `${value}px` : value;

/** 超过该位移视为拖动滚动，避免误触 Tab 点击 */
const DRAG_CLICK_SUPPRESS_PX = 4;

/**
 * 下划线 Tab：左对齐排列，文案宽度指示线，超出容器可横向滚动。
 * 禁用项使用 aria-disabled（非原生 disabled），以便 Tooltip hover 仍可触发。
 */
export function UnderlineTabs<T extends string = string>({
  activeKey,
  options,
  onChange,
  className = "",
  style,
  gap = 24,
  disabled = false,
  ariaLabel,
}: UnderlineTabsProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const labelRefs = useRef(new Map<string, HTMLSpanElement>());
  const [indicatorStyle, setIndicatorStyle] = useState<CSSProperties>({
    opacity: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startScrollLeft: number;
    moved: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);

  const updateIndicator = useCallback(() => {
    const container = containerRef.current;
    const activeLabel = labelRefs.current.get(activeKey);
    if (!container || !activeLabel) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const labelRect = activeLabel.getBoundingClientRect();

    setIndicatorStyle({
      left: labelRect.left - containerRect.left + container.scrollLeft,
      width: labelRect.width,
      opacity: 1,
    });
  }, [activeKey]);

  useLayoutEffect(() => {
    updateIndicator();
  }, [updateIndicator, options]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateIndicator();
    });

    const handleScroll = () => {
      updateIndicator();
    };

    resizeObserver.observe(container);
    labelRefs.current.forEach((label) => {
      resizeObserver.observe(label);
    });
    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      resizeObserver.disconnect();
      container.removeEventListener("scroll", handleScroll);
    };
  }, [updateIndicator, options]);

  useLayoutEffect(() => {
    labelRefs.current.get(activeKey)?.scrollIntoView({
      inline: "nearest",
      block: "nearest",
    });
  }, [activeKey, options]);

  // 鼠标滚轮转为横向滚动；按住拖动内容横向滚动
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const canScrollX = () => container.scrollWidth > container.clientWidth + 1;

    const onWheel = (event: WheelEvent) => {
      if (!canScrollX()) return;
      const delta =
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
          ? event.deltaX
          : event.deltaY;
      if (!delta) return;
      const prev = container.scrollLeft;
      const next = Math.max(
        0,
        Math.min(container.scrollWidth - container.clientWidth, prev + delta)
      );
      if (next === prev) return;
      event.preventDefault();
      container.scrollLeft = next;
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0 || !canScrollX()) return;
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: container.scrollLeft,
        moved: false,
      };
      suppressClickRef.current = false;
    };

    const onPointerMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - drag.startX;
      if (!drag.moved && Math.abs(deltaX) < DRAG_CLICK_SUPPRESS_PX) return;

      if (!drag.moved) {
        drag.moved = true;
        suppressClickRef.current = true;
        setIsDragging(true);
        try {
          container.setPointerCapture(event.pointerId);
        } catch {
          // ignore
        }
      }

      event.preventDefault();
      container.scrollLeft = drag.startScrollLeft - deltaX;
    };

    const endDrag = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      dragRef.current = null;
      setIsDragging(false);
      try {
        if (container.hasPointerCapture(event.pointerId)) {
          container.releasePointerCapture(event.pointerId);
        }
      } catch {
        // ignore
      }
    };

    const onClickCapture = (event: MouseEvent) => {
      if (!suppressClickRef.current) return;
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    container.addEventListener("pointerdown", onPointerDown);
    container.addEventListener("pointermove", onPointerMove);
    container.addEventListener("pointerup", endDrag);
    container.addEventListener("pointercancel", endDrag);
    container.addEventListener("click", onClickCapture, true);

    return () => {
      container.removeEventListener("wheel", onWheel);
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", endDrag);
      container.removeEventListener("pointercancel", endDrag);
      container.removeEventListener("click", onClickCapture, true);
    };
  }, []);

  const cssVars = {
    "--underline-tabs-gap": formatCssSize(gap),
    ...style,
  } as CSSProperties;

  return (
    <div
      ref={containerRef}
      className={`${styles.underlineTabs} ${isDragging ? styles.dragging : ""} ${className}`}
      style={cssVars}
      role="tablist"
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const itemDisabled = disabled || Boolean(option.disabled);
        const active = activeKey === option.value;

        const tabButton = (
          <button
            type="button"
            role="tab"
            aria-selected={active}
            aria-disabled={itemDisabled}
            className={`${styles.tab} ${active ? styles.active : ""} ${
              itemDisabled ? styles.disabled : ""
            }`}
            onClick={() => {
              if (itemDisabled || suppressClickRef.current) return;
              onChange(option.value);
            }}
          >
            <span
              ref={(node) => {
                if (node) {
                  labelRefs.current.set(option.value, node);
                } else {
                  labelRefs.current.delete(option.value);
                }
              }}
              className={styles.label}
            >
              {option.label}
            </span>
          </button>
        );

        return (
          <div key={option.value} className={styles.tabItem}>
            {option.tooltip ? (
              <Tooltip
                text={option.tooltip as ReactNode}
                usePortal
                placement="top"
                className={styles.tabTooltipTrigger}
              >
                {tabButton}
              </Tooltip>
            ) : (
              tabButton
            )}
          </div>
        );
      })}
      <span className={styles.indicator} style={indicatorStyle} aria-hidden />
    </div>
  );
}
